// Pure parts of the desktop link: role, route and clock estimation. No store, no socket.

import { DEVICE_TYPES } from '~/lib/constants'

export const PING_MS = 2000
// No pong for this long and a remote's link is down, though its DC is open (runner tab frozen, relay broken).
export const PONG_TIMEOUT_MS = 6000
// The direct route pings faster: silent for P2P_TIMEOUT_MS and sends fall back to the relay.
export const P2P_PING_MS = 500
export const P2P_TIMEOUT_MS = 2000
// A negotiation not open by then is a failure; failures back off along P2P_RETRY_MS.
export const P2P_CONNECT_MS = 10_000
export const P2P_RETRY_MS = [2000, 5000, 15_000, 30_000]
// Clock estimate keeps the lowest-RTT sample of the last few: the least queueing, the truest offset.
export const CLOCK_SAMPLES = 8

/** This client's end of the link, from the server's echo of it in `presence.connections`: 'remote', 'runner' or null. */
export const linkRole = ({ me, runner, connections }) => {
	if (connections?.find(c => c.name === me)?.device_type === DEVICE_TYPES.REMOTE) return 'remote'
	if (runner && runner === me) return 'runner'
	return null
}

/** Whether a direct route last heard from at `p2pAt` carries sends. */
export const p2pLive = ({ p2pAt, now, forceRelay }) => !forceRelay && p2pAt != null && now - p2pAt <= P2P_TIMEOUT_MS

/** 'p2p' | 'relay' | 'down'. A remote is up only once the runner it names has answered a ping on this connection, on either route; the runner's is its DC (per-remote routes are `p2pLive`). */
export const linkRoute = ({ role, dcOpen, runner, connections, lastPongAt, p2pAt, now, forceRelay }) => {
	if (!role || !dcOpen) return 'down'
	if (role === 'runner') return 'relay'
	if (!runner || !connections?.some(c => c.name === runner)) return 'down'
	if (p2pLive({ p2pAt, now, forceRelay })) return 'p2p'
	if (lastPongAt == null || now - lastPongAt > PONG_TIMEOUT_MS) return 'down'
	return 'relay'
}

/** Delay before the next direct attempt after `failures` consecutive failed ones. */
export const p2pRetryDelay = failures => P2P_RETRY_MS[Math.min(Math.max(failures - 1, 0), P2P_RETRY_MS.length - 1)]

/** One ping/pong: sent at local `t0`, stamped by the runner at `t1`, back at local `t2`. `offset` = runner clock − local clock. */
export const clockSample = (t0, t1, t2) => ({ rtt: t2 - t0, offset: t1 - (t0 + t2) / 2 })

/** Adds `sample` to the window and returns `{ samples, rtt, offset }`: `rtt` is the latest, `offset` the lowest-RTT sample's. */
export const addClockSample = (samples, sample) => {
	const next = [...samples, sample].slice(-CLOCK_SAMPLES)
	const best = next.reduce((a, b) => (b.rtt < a.rtt ? b : a))
	return { samples: next, rtt: sample.rtt, offset: best.offset }
}
